import { useState, type FormEvent } from "react";
import type { PlatformMfaPrompt } from "./admin-auth";

const qrImageSource = (value: string): string | undefined => {
  const trimmed = value.trim();
  if (trimmed.startsWith("data:image/svg+xml")) return trimmed;
  if (trimmed.startsWith("<svg") && trimmed.endsWith("</svg>")) {
    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(trimmed)}`;
  }
  return undefined;
};

export function PlatformMfa({ prompt, busy, error, onVerify, onCancel }: Readonly<{
  prompt: PlatformMfaPrompt;
  busy: boolean;
  error?: string;
  onVerify: (code: string) => void;
  onCancel: () => void;
}>) {
  const [code, setCode] = useState("");
  const qrSource = prompt.kind === "enroll" ? qrImageSource(prompt.qrCode) : undefined;
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!busy && /^\d{6}$/u.test(code)) onVerify(code);
  };
  return <>
    <p className="eyebrow">Two-step verification</p>
    <h1>{prompt.kind === "enroll" ? "Protect owner access" : "Verify your authenticator"}</h1>
    {prompt.kind === "enroll" ? <div className="mfa-enrollment">
      <p>Scan this QR code with an authenticator app. This setup stays in this browser and is never sent to Phenera.</p>
      {qrSource === undefined
        ? <p className="mfa-qr-unavailable">The QR image could not be displayed. Enter the one-time setup key below in your authenticator app.</p>
        : <img className="mfa-qr" src={qrSource} alt="Authenticator setup QR code" />}
      <div className="mfa-secret"><span>One-time setup key</span><code>{prompt.secret}</code><small>Do not share or save this key after setup.</small></div>
    </div> : <p>Enter the current six-digit code from <strong>{prompt.factorName}</strong>.</p>}
    <form data-control-id="admin.auth.mfa.form" onSubmit={submit}>
      <label><span>Authenticator code</span><input data-control-id="admin.auth.mfa.code" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}" minLength={6} maxLength={6} required value={code} onChange={(event) => setCode(event.target.value.replace(/\D/gu, "").slice(0, 6))} /></label>
      {error !== undefined && <p className="error" role="alert">{error}</p>}
      <div className="mfa-actions">
        <button data-control-id="admin.auth.mfa.cancel" type="button" className="secondary" disabled={busy} title={busy ? "Wait for the current authenticator request to finish." : prompt.kind === "enroll" ? "Remove this unverified authenticator and return to sign-in." : "End this sign-in without changing your authenticator."} onClick={onCancel}>Cancel</button>
        <button data-control-id="admin.auth.mfa.verify" type="submit" className="primary" disabled={busy || code.length !== 6} title={busy ? "Wait for the current authenticator request to finish." : code.length !== 6 ? "Enter exactly six digits." : "Verify this authenticator code and open the owner console."}>{busy ? "Verifying…" : prompt.kind === "enroll" ? "Enable and continue" : "Verify and continue"}</button>
      </div>
    </form>
  </>;
}
